import { Menu, X } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation"; // Import useRouter
import { useTheme } from "next-themes";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a"> & { isLight?: boolean }
>(({ className, title, children, isLight, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors",
            className,
          )}
          style={{
            backgroundColor: "transparent",
            color: isLight ? "#111827" : "#ffffff",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isLight
              ? "#f3f4f6"
              : "#1f2937";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          {...props}
        >
          <div
            className="text-sm font-medium leading-none"
            style={{ color: isLight ? "#111827" : "#ffffff" }}
          >
            {title}
          </div>
          <p
            className="line-clamp-2 text-sm leading-snug"
            style={{ color: isLight ? "#4b5563" : "#9ca3af" }}
          >
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export function MainMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter(); // Instantiate router
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isLight = mounted && theme === "light";
  const isDark = mounted && theme === "dark";

  const toggleMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  }, []);

  const handleNavigation = useCallback((href: string) => {
    window.open(href, "_blank");
    setIsOpen(false);
  }, []);

  // Close menu when clicking outside
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest("[data-menu-content]") &&
        !target.closest("[data-menu-button]")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Define menu items (products live in the dropdowns below)
  const menuItems = [
    { href: "/blog", label: "Blog" },
    { href: "/docs", label: "Docs" },
    { href: "/investors", label: "Investors" },
    {
      href: "https://calendar.app.google/4E1JwtdJMcbrdG1D8",
      label: "Talk to the Founder",
      isButton: true,
    },
  ];

  return (
    <div className="relative text-foreground">
      {/* Desktop Navigation Menu */}
      <div className="hidden md:block">
        <NavigationMenu>
          <NavigationMenuList className="gap-1">
            <NavigationMenuItem>
              <NavigationMenuTrigger
                className="text-sm font-medium transition-colors !bg-transparent hover:!bg-transparent data-[state=open]:!bg-transparent"
                style={{
                  color: isLight ? "#111827" : "#ffffff",
                }}
              >
                Kautious
              </NavigationMenuTrigger>
              <NavigationMenuContent
                className="shadow-lg"
                style={{
                  backgroundColor: isLight ? "#ffffff" : "#030712",
                  borderColor: isLight ? "#e5e7eb" : "#1f2937",
                }}
              >
                <ul
                  className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]"
                  style={{
                    backgroundColor: isLight ? "#ffffff" : "#030712",
                  }}
                >
                  <li className="row-span-4">
                    <NavigationMenuLink asChild>
                      <a
                        className="flex h-full w-full select-none flex-col justify-end rounded-md p-6 no-underline outline-none focus:shadow-md transition-colors"
                        href="/kautious"
                        style={{
                          backgroundColor: isLight ? "#f3f4f6" : "#111827",
                          borderColor: isLight ? "#e5e7eb" : "#1f2937",
                          color: isLight ? "#111827" : "#ffffff",
                        }}
                      >
                        <div
                          className="mb-2 mt-4 text-lg font-medium"
                          style={{ color: isLight ? "#111827" : "#ffffff" }}
                        >
                          Kautious
                        </div>
                        <p
                          className="text-sm leading-tight"
                          style={{ color: isLight ? "#4b5563" : "#9ca3af" }}
                        >
                          Build financial tools from plain English. Build the
                          tool, skip the ticket.
                        </p>
                      </a>
                    </NavigationMenuLink>
                  </li>
                  <ListItem href="/kautious" title="Overview" isLight={isLight}>
                    Build sheets, monitors, reports, and apps from plain English
                  </ListItem>
                  <ListItem
                    href="/desktop"
                    title="Desktop App"
                    isLight={isLight}
                  >
                    Native macOS research — source-grounded, read-only,
                    auditable
                  </ListItem>
                  <ListItem href="/console" title="Console" isLight={isLight}>
                    Access your workspace, tools, and dashboards
                  </ListItem>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Daytta dropdown — the data platform and its engines */}
            <NavigationMenuItem>
              <NavigationMenuTrigger
                className="text-sm font-medium transition-colors !bg-transparent hover:!bg-transparent data-[state=open]:!bg-transparent"
                style={{
                  color: isLight ? "#111827" : "#ffffff",
                }}
              >
                Daytta
              </NavigationMenuTrigger>
              <NavigationMenuContent
                className="shadow-lg"
                style={{
                  backgroundColor: isLight ? "#ffffff" : "#030712",
                  borderColor: isLight ? "#e5e7eb" : "#1f2937",
                }}
              >
                <ul
                  className="grid gap-2 p-4 w-[340px]"
                  style={{
                    backgroundColor: isLight ? "#ffffff" : "#030712",
                  }}
                >
                  <ListItem href="/daytta" title="Overview" isLight={isLight}>
                    The data-heavy analytics platform, on the Llamox runtime
                  </ListItem>
                  <ListItem
                    href="/extract"
                    title="Kautious Extract"
                    isLight={isLight}
                  >
                    Turn messy documents into defensible structured data
                  </ListItem>
                  <ListItem
                    href="/graph"
                    title="Kautious Graph"
                    isLight={isLight}
                  >
                    Temporal memory for production AI agents
                  </ListItem>
                  <ListItem
                    href="/time"
                    title="Kautious Time"
                    isLight={isLight}
                  >
                    Backtested time-series forecasting
                  </ListItem>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Llamox — the governed orchestration backend */}
            <NavigationMenuItem>
              <Link
                href="/llamox"
                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium transition-colors hover:opacity-70"
                style={{
                  color: isLight ? "#111827" : "#ffffff",
                }}
              >
                Llamox
              </Link>
            </NavigationMenuItem>

            {/* Regular navigation links - clean text style */}
            {menuItems
              .filter((item) => !item.isButton)
              .map((item) => (
                <NavigationMenuItem key={item.href}>
                  <Link
                    href={item.href}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium transition-colors hover:opacity-70"
                    style={{
                      color: isLight ? "#111827" : "#ffffff",
                    }}
                  >
                    {item.label}
                  </Link>
                </NavigationMenuItem>
              ))}

            {/* CTA Button - styled prominently */}
            <NavigationMenuItem>
              <Link
                href="https://calendar.app.google/4E1JwtdJMcbrdG1D8"
                target="_blank"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow-md ml-2 hover:opacity-90"
                style={{
                  backgroundColor: isLight ? "#111827" : "#ffffff",
                  color: isLight ? "#ffffff" : "#111827",
                }}
              >
                Talk to the Founder
              </Link>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {/* Mobile Menu Button */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={toggleMenu}
          data-menu-button
          className="relative flex items-center justify-center w-10 h-10 rounded-md border transition-colors"
          style={{
            backgroundColor: isLight ? "#ffffff" : "#030712",
            borderColor: isLight ? "#e5e7eb" : "#1f2937",
            color: isLight ? "#111827" : "#ffffff",
          }}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
        >
          <span className="sr-only">Toggle menu</span>
          <div className="relative w-5 h-5">
            <Menu
              className={cn(
                "absolute inset-0 w-5 h-5 transition-all duration-200",
                isOpen
                  ? "opacity-0 rotate-90 scale-75"
                  : "opacity-100 rotate-0 scale-100",
              )}
            />
            <X
              className={cn(
                "absolute inset-0 w-5 h-5 transition-all duration-200",
                isOpen
                  ? "opacity-100 rotate-0 scale-100"
                  : "opacity-0 -rotate-90 scale-75",
              )}
            />
          </div>
        </button>

        {/* Mobile Menu Overlay */}
        <div
          className={cn(
            "fixed inset-0 z-50 md:hidden transition-opacity duration-300",
            isOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none",
          )}
          style={{ top: "65px" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu Panel */}
          <div
            data-menu-content
            className={cn(
              "absolute right-0 w-full max-w-sm backdrop-blur-xl border-l shadow-[0_24px_80px_rgba(0,0,0,0.25)] h-[calc(100vh-65px)] transition-transform duration-300 ease-out overflow-y-auto",
              isOpen ? "translate-x-0" : "translate-x-full",
            )}
            style={{
              backgroundColor: isLight
                ? "rgba(255, 255, 255, 0.95)"
                : "rgba(3, 7, 18, 0.95)",
              color: isLight ? "#111827" : "#f9fafb",
              borderColor: isLight
                ? "rgba(229, 231, 235, 0.6)"
                : "rgba(31, 41, 55, 0.6)",
            }}
          >
            <div
              className="sticky top-0 z-10 flex items-start justify-between px-6 py-4 border-b backdrop-blur-xl"
              style={{
                borderColor: isLight
                  ? "rgba(229, 231, 235, 0.6)"
                  : "rgba(31, 41, 55, 0.6)",
                backgroundColor: isLight
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(3, 7, 18, 0.95)",
              }}
            >
              <div>
                <p
                  className="text-[0.65rem] font-semibold uppercase tracking-[0.2em]"
                  style={{ color: isLight ? "#6b7280" : "#9ca3af" }}
                >
                  Menu
                </p>
                <p
                  className="text-sm"
                  style={{ color: isLight ? "#6b7280" : "#9ca3af" }}
                >
                  Explore the platform
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="hover:bg-accent"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <nav className="flex flex-col p-6 gap-6">
              <div
                className="rounded-2xl border p-4 shadow-sm"
                style={{
                  borderColor: isLight
                    ? "rgba(229, 231, 235, 0.7)"
                    : "rgba(31, 41, 55, 0.7)",
                  backgroundColor: isLight ? "#f9fafb" : "#111827",
                }}
              >
                <p
                  className="text-xs font-semibold mb-1"
                  style={{ color: isLight ? "#111827" : "#f9fafb" }}
                >
                  Kautious
                </p>
                <p
                  className="text-sm"
                  style={{ color: isLight ? "#6b7280" : "#9ca3af" }}
                >
                  Build financial tools from plain English.
                </p>
              </div>

              {/* AI Section */}
              <div>
                <h3
                  className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] mb-3"
                  style={{ color: isLight ? "#6b7280" : "#9ca3af" }}
                >
                  Products
                </h3>
                <div className="space-y-2">
                  {[
                    {
                      path: "/kautious",
                      name: "Kautious",
                      desc: "Build financial tools from plain English",
                      children: [
                        {
                          path: "/desktop",
                          name: "Desktop App",
                          desc: "The AI desktop workspace for the desk",
                        },
                        {
                          path: "/console",
                          name: "Console",
                          desc: "Your workspace, tools, and dashboards",
                        },
                      ],
                    },
                    {
                      path: "/daytta",
                      name: "Daytta",
                      desc: "The data-heavy analytics platform",
                      children: [
                        {
                          path: "/extract",
                          name: "Kautious Extract",
                          desc: "Messy documents into defensible data",
                        },
                        {
                          path: "/graph",
                          name: "Kautious Graph",
                          desc: "Temporal memory for AI agents",
                        },
                        {
                          path: "/time",
                          name: "Kautious Time",
                          desc: "Backtested time-series forecasting",
                        },
                      ],
                    },
                    {
                      path: "/llamox",
                      name: "Llamox",
                      desc: "The governed AI operations platform",
                    },
                  ].map((item) => (
                    <React.Fragment key={item.path}>
                      <button
                        onClick={() => {
                          router.push(item.path);
                          setIsOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 rounded-xl border transition-colors group"
                        style={{
                          borderColor: isLight
                            ? "rgba(229, 231, 235, 0.7)"
                            : "rgba(31, 41, 55, 0.7)",
                          backgroundColor: isLight
                            ? "rgba(249, 250, 251, 0.5)"
                            : "rgba(31, 41, 55, 0.5)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isLight
                            ? "rgba(243, 244, 246, 1)"
                            : "rgba(55, 65, 81, 1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isLight
                            ? "rgba(249, 250, 251, 0.5)"
                            : "rgba(31, 41, 55, 0.5)";
                        }}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: isLight ? "#111827" : "#f9fafb" }}
                        >
                          {item.name}
                        </div>
                        <div
                          className="text-xs mt-0.5"
                          style={{ color: isLight ? "#6b7280" : "#9ca3af" }}
                        >
                          {item.desc}
                        </div>
                      </button>
                      {"children" in item &&
                        item.children?.map((child) => (
                          <button
                            key={child.path}
                            onClick={() => {
                              router.push(child.path);
                              setIsOpen(false);
                            }}
                            className="w-full text-left pl-8 pr-4 py-2.5 rounded-xl border transition-colors group ml-2 border-l-2"
                            style={{
                              borderColor: isLight
                                ? "rgba(229, 231, 235, 0.7)"
                                : "rgba(31, 41, 55, 0.7)",
                              borderLeftColor: isLight
                                ? "rgba(22, 163, 74, 0.4)"
                                : "rgba(34, 197, 94, 0.4)",
                              backgroundColor: isLight
                                ? "rgba(249, 250, 251, 0.3)"
                                : "rgba(31, 41, 55, 0.3)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isLight
                                ? "rgba(243, 244, 246, 1)"
                                : "rgba(55, 65, 81, 1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isLight
                                ? "rgba(249, 250, 251, 0.3)"
                                : "rgba(31, 41, 55, 0.3)";
                            }}
                          >
                            <div
                              className="font-medium text-sm"
                              style={{
                                color: isLight ? "#111827" : "#f9fafb",
                              }}
                            >
                              {child.name}
                            </div>
                            <div
                              className="text-xs mt-0.5"
                              style={{
                                color: isLight ? "#6b7280" : "#9ca3af",
                              }}
                            >
                              {child.desc}
                            </div>
                          </button>
                        ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div
                className="h-px"
                style={{
                  backgroundColor: isLight
                    ? "rgba(229, 231, 235, 0.8)"
                    : "rgba(31, 41, 55, 0.8)",
                }}
              />

              {/* Main Menu Items */}
              <div className="space-y-2">
                {menuItems
                  .filter((item) => !item.isButton)
                  .map((item) => (
                    <button
                      key={item.href}
                      onClick={() => {
                        if (item.href.startsWith("http")) {
                          window.open(item.href, "_blank");
                        } else {
                          router.push(item.href);
                        }
                        setIsOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm font-medium rounded-xl border transition-colors shadow-sm"
                      style={{
                        borderColor: isLight
                          ? "rgba(229, 231, 235, 0.7)"
                          : "rgba(31, 41, 55, 0.7)",
                        backgroundColor: isLight ? "#ffffff" : "#030712",
                        color: isLight ? "#111827" : "#f9fafb",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isLight
                          ? "#f3f4f6"
                          : "#1f2937";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isLight
                          ? "#ffffff"
                          : "#030712";
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
              </div>

              {/* CTA Button */}
              <div
                className="pt-6 border-t"
                style={{
                  borderColor: isLight
                    ? "rgba(229, 231, 235, 0.6)"
                    : "rgba(31, 41, 55, 0.6)",
                }}
              >
                <button
                  onClick={() => {
                    window.open(
                      "https://calendar.app.google/4E1JwtdJMcbrdG1D8",
                      "_blank",
                    );
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 rounded-xl text-sm font-semibold transition-colors shadow-md hover:opacity-90"
                  style={{
                    backgroundColor: isLight ? "#111827" : "#ffffff",
                    color: isLight ? "#ffffff" : "#111827",
                  }}
                >
                  Talk to the Founder
                </button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
